import { useState } from 'react';
import {
    BoundedContent,
    Box,
    FlexBox,
    H2,
    TabContent,
    TabHeader,
    Tabs,
    TabsContents,
    TabsHeaders,
} from '@vp/swan';
import { __url } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { isNNLogoEssential } from '../../lib/presenter/productsPresenter';
import { getNavigationData, useCrossPlatformTracking } from '../../lib/tracking';
import { ProductGrid } from '../ProductGrid';
import { CreateAProjectCTA } from './CreateAProjectCTA/CreateAProjectCTA';
import { SeeAllTile } from './SeeAllTile/SeeAllTile';
import { categoryPageConfig } from './categoryPageConfig';
import type { SupportedLocale } from '@99designs/i18n';
import type { DesignProductPresenter } from '../../lib/presenter/types';
import type { ProductGroups } from '../../types';

interface DesignCategoriesProps {
    title: string;
    categories: ProductGroups;
    locale: SupportedLocale;
    initialCategoryId?: string;
}

export function DesignCategories({
    title,
    categories,
    initialCategoryId,
    locale,
}: DesignCategoriesProps) {
    const { pageData } = usePage();
    const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
        if (categories.length === 0) {
            return '';
        }
        return (
            categories.find((category) => category.id === initialCategoryId)?.id ?? categories[0].id
        );
    });
    const selectedCategory = categories.find((category) => category.id === selectedCategoryId);
    const selectedCategoryPageConfig =
        categoryPageConfig[selectedCategoryId as keyof typeof categoryPageConfig];

    const pageZone = 'Design Category Tiles';
    const crossPlatformTracking = useCrossPlatformTracking(locale, pageZone);

    return (
        <BoundedContent data-testid="design-categories">
            <Box paddingX={{ xs: 3, md: 0 }}>
                <H2 marginBottom={6} data-testid="design-categories-title">
                    {title}
                </H2>
                <Tabs
                    defaultSelectedTabId={selectedCategoryId}
                    aria-label={title}
                    selectedTabId={selectedCategoryId}
                    onRequestTabChange={(requestedTabId) =>
                        requestedTabId && setSelectedCategoryId(requestedTabId)
                    }
                    showDividingLine={false}
                    data-testid="design-categories-tabs"
                >
                    <TabsHeaders data-testid="design-categories-tab-list">
                        {categories.map((category) => (
                            <TabHeader
                                key={category.id}
                                tabId={category.id}
                                data-testid={`design-categories-tab-${category.id}`}
                            >
                                {category.name}
                            </TabHeader>
                        ))}
                    </TabsHeaders>
                    <TabsContents>
                        {categories.map((category) => (
                            <TabContent
                                key={category.id}
                                tabId={category.id}
                                data-testid={`design-categories-panel-${category.id}`}
                            >
                                <Box paddingBottom="6">
                                    <ProductGrid
                                        key={
                                            // Force a re-render to prevent flickering when active tab changes
                                            category.id === selectedCategoryId ? category.id : ''
                                        }
                                        products={category.products ?? []}
                                        locale={locale}
                                        SeeAllTile={
                                            selectedCategoryPageConfig &&
                                            selectedCategoryPageConfig.availableLocales.includes(
                                                locale
                                            ) ? (
                                                <SeeAllTile
                                                    href={__url(selectedCategoryPageConfig.path)}
                                                    trackingData={{
                                                        category: pageData.pageSection,
                                                        label: `Content: ${pageZone}`,
                                                        eventDetail: `${pageData.pagePath};${selectedCategoryPageConfig.path};${pageZone};${selectedCategoryPageConfig.path}`,
                                                        navigationDetail: `${category.name}|See all:${category.products.length}`,
                                                        pageSection: pageData.pageSection,
                                                        pageStage: pageData.pageStage,
                                                        pageName: pageData.pageName,
                                                    }}
                                                />
                                            ) : undefined
                                        }
                                        productClickedTrackingData={(
                                            product: DesignProductPresenter,
                                            index: number
                                        ) => {
                                            return getNavigationData(product, {
                                                pageName: pageData.pageName,
                                                pageStage: pageData.pageStage,
                                                pageSection: pageData.pageSection,
                                                pagePath:
                                                    pageData.pagePath ?? window.location.pathname,
                                                pageZone,
                                                position: index,
                                                linkHierarchy: selectedCategory?.name,
                                                crossPlatformTracking: isNNLogoEssential(
                                                    product,
                                                    locale
                                                )
                                                    ? crossPlatformTracking?.toString()
                                                    : undefined,
                                            });
                                        }}
                                    />
                                </Box>
                            </TabContent>
                        ))}
                    </TabsContents>
                </Tabs>
            </Box>

            <FlexBox
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                data-testid="design-categories-create-project"
            >
                <CreateAProjectCTA />
            </FlexBox>
        </BoundedContent>
    );
}
